import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Body, Modal, ModalBody, ModalFooter, ModalHeader, Subhead } from '@growtherapy/sprout-ui';

import { useOpenVisitorModalCallback } from '../../modals/useOpenVisitorModalCallback';
import { VisitorState, visitorStateAtom } from '../../state';
import { isAdmissionModalOpenAtom } from './state';
import { useAdmissionTrackingEvents } from './useAdmissionTrackingEvents';
import { useVisitorConnectRoomCallback } from './useVisitorConnectRoomCallback';

export function AdmissionModal({
  isOpen,
  onJoin: handleJoin,
  onDismiss: handleDismiss,
  isLoading,
}: {
  isOpen: boolean;
  onJoin: () => void;
  onDismiss: () => void;
  isLoading: boolean;
}) {
  const t = useTranslate();
  return (
    <Modal className='sm:mt-[10%]' isOpen={isOpen} onClose={isLoading ? undefined : handleDismiss}>
      {/* Empty header provides the "X" close button */}
      <ModalHeader className='rebrand:h-0 rebrand:relative rebrand:top-200'>
        <span />
      </ModalHeader>
      <ModalBody className='flex flex-col gap-2 items-center'>
        <Subhead.Large>{t('telehealth-core:waitingRoom.letsStartYourSession')}</Subhead.Large>
        <Body.Default>{t('telehealth-core:waitingRoom.yourClinicianIsReadyToBegin')}</Body.Default>
      </ModalBody>
      <ModalFooter
        className='flex justify-center'
        actions={[
          {
            key: 'join-session',
            'aria-label': isLoading
              ? t('telehealth-core:waitingRoom.joiningSession')
              : t('telehealth-core:waitingRoom.joinSession'),
            use: 'primary',
            loading: isLoading,
            onClick: handleJoin,
            actionContent: t('telehealth-core:waitingRoom.joinSession'),
          },
        ]}
      />
    </Modal>
  );
}

export function AdmissionModalWrapper() {
  const isOpen = useAtomValue(isAdmissionModalOpenAtom);
  const connectRoom = useVisitorConnectRoomCallback();
  const isLoading = useAtomValue(visitorStateAtom) === VisitorState.JOINING;
  const openVisitorModal = useOpenVisitorModalCallback();
  const { trackAdmissionPromptViewed, trackJoinSessionClicked, trackAdmissionPromptDismissed } =
    useAdmissionTrackingEvents();

  useEffect(
    function handleTrackingAdmissionPromptViewed() {
      if (isOpen) {
        trackAdmissionPromptViewed();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen],
  );

  return (
    <AdmissionModal
      isOpen={isOpen}
      onJoin={() => {
        trackJoinSessionClicked();
        connectRoom();
      }}
      onDismiss={() => {
        trackAdmissionPromptDismissed();
        openVisitorModal(null);
      }}
      isLoading={isLoading}
    />
  );
}
